import * as React from 'react';
import moment from 'moment';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from 'Src/models/tournament';
import { getTournamentManagementSummary } from 'Src/helpers/service/adminSU/tournamentsTable';
import { getTournamentPublicSiteLink, getTournamentTVPublicSiteLink } from 'Src/helpers/link/link';
import { getTournamentCapacitySummary } from 'Src/helpers/tournament/tournament';

interface Props {
  user: AppUser;
  tournament: Tournament;
  onOpenSection: (path: string) => void;
  onEdit: () => void;
}

interface Metrics {
  eligible: number;
  invited: number;
  accepted: number;
  rejected: number;
  participants: number;
  teams: number;
  waiting: number;
  pending: number;
  outdated: number;
}

interface State {
  metrics: Metrics;
  isLoading: boolean;
}

const EMPTY_METRICS: Metrics = {
  eligible: 0,
  invited: 0,
  accepted: 0,
  rejected: 0,
  participants: 0,
  teams: 0,
  waiting: 0,
  pending: 0,
  outdated: 0
};

const Icon = ({ path, size = 14 }: { path: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d={path} />
  </svg>
);

const ICONS = {
  hourglass: 'M6 2h12M6 22h12M6 2c0 4 6 6 6 10s-6 6-6 10M18 2c0 4-6 6-6 10s6 6 6 10',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  chevron: 'M9 6l6 6-6 6',
  check: 'M20 6 9 17l-5-5',
  more: 'M12 6h.01M12 12h.01M12 18h.01',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z'
};

export class TournamentOverview extends React.Component<Props, State> {
  state: State = { metrics: EMPTY_METRICS, isLoading: true };

  componentDidMount() {
    const { user, tournament } = this.props;
    const id = tournament.id;

    getTournamentManagementSummary(user, id)
      .then(summary => {
        this.setState({
          metrics: {
            eligible: summary.eligible,
            invited: Math.max(0, summary.eligible - summary.notInvited),
            accepted: summary.accepted,
            rejected: summary.rejected,
            participants: summary.participants,
            teams: summary.teams,
            waiting: summary.waiting,
            pending: summary.confirmationPending,
            outdated: summary.confirmationOutdated
          },
          isLoading: false
        });
      })
      .catch(() => this.setState({ isLoading: false }));
  }

  renderKpi(label: string, value: string | number, note: string, path: string, alert = false) {
    return (
      <button className="utmOverview__kpi" type="button" onClick={() => this.props.onOpenSection(path)}>
        <span className="utmOverview__label">{label}</span>
        <span className="utmOverview__kpiValue">
          <strong className={alert ? 'is-alert' : ''}>{value}</strong>
          <small>{note}</small>
        </span>
      </button>
    );
  }

  render() {
    const { tournament } = this.props;
    const { metrics, isLoading } = this.state;
    const settings = tournament.autoEnrollmentTeamsSettings || [];
    const { booked, capacity, unlimited: isUnlimited } = getTournamentCapacitySummary(tournament, metrics.participants);
    const requests = metrics.pending + metrics.outdated;
    const funnel = [
      { label: 'Eligible schools', value: metrics.eligible, colour: '#94a3b8', path: 'eligibleSchools' },
      { label: 'Invited', value: metrics.invited, colour: '#38b6cf', path: 'eligibleSchools' },
      { label: 'Accepted', value: metrics.accepted, colour: '#2fa56b', path: 'eligibleSchools' },
      { label: 'Participants', value: metrics.participants, colour: '#227ebd', path: 'participants' },
      { label: 'Teams entered', value: metrics.teams, colour: '#176a9d', path: 'teams' }
    ];
    const funnelMax = Math.max(...funnel.map(item => item.value), 1);
    const checklist = [
      {
        label: 'Capacity and enrollment rules set',
        done: !tournament.isAutoEnrollmentTeams || settings.length > 0,
        path: 'participants'
      },
      { label: 'Eligible schools chosen', done: metrics.eligible > 0, path: 'eligibleSchools' },
      {
        label: 'Invites sent to every eligible school',
        done: metrics.eligible > 0 && metrics.invited === metrics.eligible,
        hint: `${Math.max(0, metrics.eligible - metrics.invited)} schools not invited yet`,
        path: 'eligibleSchools'
      },
      {
        label: 'Confirmation requests answered',
        done: requests === 0,
        hint: `${metrics.pending} awaiting · ${metrics.outdated} outdated`,
        path: 'confirmationRequests'
      },
      { label: 'Groups created', done: (tournament.customGroups || []).length > 0, path: 'customGroups' },
      { label: 'Competition stages created', done: (tournament.tournamentLayout || []).length > 0, path: 'grid' },
      { label: 'Published on union website', done: tournament.isPublishOnTheWebsite, path: 'publishing' }
    ];
    const doneCount = checklist.filter(item => item.done).length;
    const loadingClass = isLoading ? ' is-loading' : '';
    const eventStartsAt = moment(tournament.startTime);
    const eventEndsAt = moment(tournament.endTime);
    const eventPeriod = eventStartsAt.isSame(eventEndsAt, 'day')
      ? `${eventStartsAt.format('DD MMM YYYY')} · ${eventStartsAt.format('HH:mm')} – ${eventEndsAt.format('HH:mm')}`
      : `${eventStartsAt.format('DD MMM YYYY')} · ${eventStartsAt.format('HH:mm')} → ${eventEndsAt.format(
          'DD MMM YYYY'
        )} · ${eventEndsAt.format('HH:mm')}`;
    const bookingWindow =
      tournament.entriesOpen && tournament.deadlineForEntries
        ? `${moment(tournament.entriesOpen).format('DD MMM YYYY · HH:mm')} → ${moment(
            tournament.deadlineForEntries
          ).format('DD MMM YYYY · HH:mm')}`
        : 'Not configured';
    const publicSiteUrl = tournament.domain ? getTournamentPublicSiteLink(tournament) : '';
    const bigscreenUrl = tournament.domain ? getTournamentTVPublicSiteLink(tournament) : '';
    const isPublicSiteEnabled = Boolean(publicSiteUrl) && tournament.publicSite?.status !== 'DISABLED';
    const isBigscreenEnabled = Boolean(bigscreenUrl) && tournament.publicBigscreenSite?.status !== 'DISABLED';

    return (
      <main className={`utmOverview${loadingClass}`}>
        {requests > 0 ? (
          <div className="utmOverview__requestAlert">
            <span className="utmOverview__requestIcon">
              <Icon path={ICONS.hourglass} size={18} />
            </span>
            <div>
              <strong>
                {metrics.pending} confirmation requests awaiting a reply
                {metrics.outdated ? `, ${metrics.outdated} outdated` : ''}
              </strong>
              <small>Review outstanding responses before finalising the tournament.</small>
            </div>
            <button type="button" onClick={() => this.props.onOpenSection('confirmationRequests')}>
              <Icon path={ICONS.arrow} /> Review requests
            </button>
          </div>
        ) : null}

        <section className="utmOverview__kpis">
          {this.renderKpi(
            'Places',
            isUnlimited ? String(booked) : `${booked} / ${capacity}`,
            isUnlimited ? 'unlimited' : `${Math.max(0, capacity - booked)} left`,
            'participants',
            !isUnlimited && booked >= capacity
          )}
          {this.renderKpi(
            'Participant schools',
            metrics.participants,
            `of ${metrics.eligible} eligible`,
            'participants'
          )}
          {this.renderKpi(
            'Awaiting response',
            requests,
            `${metrics.pending} pending`,
            'confirmationRequests',
            requests > 0
          )}
          {this.renderKpi('Waiting list', metrics.waiting, metrics.waiting ? 'schools queued' : 'empty', 'waitingList')}
        </section>

        <section className="utmOverview__columns">
          <article className="utmOverview__panel">
            <header>
              <div>
                <h2>Entry funnel</h2>
                <p>How far eligible schools have moved towards a confirmed entry.</p>
              </div>
            </header>
            <div className="utmOverview__funnel">
              {funnel.map(item => (
                <button key={item.label} type="button" onClick={() => this.props.onOpenSection(item.path)}>
                  <span>{item.label}</span>
                  <i>
                    <b style={{ width: `${Math.max(4, (item.value / funnelMax) * 100)}%`, background: item.colour }} />
                  </i>
                  <strong>{item.value}</strong>
                </button>
              ))}
              {metrics.rejected > 0 ? (
                <p>
                  {metrics.rejected} {metrics.rejected === 1 ? 'school declined' : 'schools declined'} the invite.
                </p>
              ) : null}
            </div>
          </article>

          <article className="utmOverview__panel">
            <header>
              <div>
                <h2>Setup checklist</h2>
                <p>
                  {doneCount} of {checklist.length} done
                </p>
              </div>
            </header>
            <div className="utmOverview__checklist">
              {checklist.map(item => (
                <button key={item.label} type="button" onClick={() => this.props.onOpenSection(item.path)}>
                  <i className={item.done ? 'is-done' : ''}>
                    <Icon path={item.done ? ICONS.check : ICONS.more} size={13} />
                  </i>
                  <span>
                    <strong>{item.label}</strong>
                    {!item.done && item.hint ? <small>{item.hint}</small> : null}
                  </span>
                  <Icon path={ICONS.chevron} />
                </button>
              ))}
            </div>
          </article>
        </section>

        <article className="utmOverview__panel utmOverview__glance">
          <header>
            <div>
              <h2>At a glance</h2>
            </div>
            <button type="button" onClick={this.props.onEdit}>
              <Icon path={ICONS.edit} /> Edit details
            </button>
          </header>
          <div className="utmOverview__facts">
            <div>
              <span>Event</span>
              <strong>{eventPeriod}</strong>
            </div>
            <div>
              <span>Booking window</span>
              <strong>{bookingWindow}</strong>
            </div>
            <div>
              <span>Venue</span>
              <strong>{tournament.venue?.postcodeData?.postcode || tournament.venue?.postcode || 'Not set'}</strong>
            </div>
            <div>
              <span>Organisers</span>
              <strong>{tournament.organisers || '—'}</strong>
            </div>
            <div>
              <span>Organisers email</span>
              <strong>
                {tournament.organisersEmail ? (
                  <a href={`mailto:${tournament.organisersEmail}`}>{tournament.organisersEmail}</a>
                ) : (
                  'Not set'
                )}
              </strong>
            </div>
            <div>
              <span>Max teams per school</span>
              <strong>{tournament.maximumNumberTeamsOfSchool || 'Unlimited'}</strong>
            </div>
            <div>
              <span>Union website listing</span>
              <strong>{tournament.isPublishOnTheWebsite ? 'Published' : 'Not published'}</strong>
            </div>
            <div>
              <span>Public site</span>
              <strong>
                {isPublicSiteEnabled ? (
                  <a href={publicSiteUrl} target="_blank" rel="noopener noreferrer">
                    Open public site
                  </a>
                ) : (
                  'Not enabled'
                )}
              </strong>
            </div>
            <div>
              <span>Bigscreen / TV</span>
              <strong>
                {isBigscreenEnabled ? (
                  <a href={bigscreenUrl} target="_blank" rel="noopener noreferrer">
                    Open Bigscreen
                  </a>
                ) : (
                  'Not enabled'
                )}
              </strong>
            </div>
          </div>
        </article>
      </main>
    );
  }
}
